/**
 * Jméno nástroje pro akci nad instancí — jedno pravidlo pro celé UI.
 *
 * Instance, která patří projektu orchestrátoru, se NESMÍ volat pod modulovým
 * jménem — platforma to odmítne s `project_qualified_tool_required` a řekne,
 * pod jakým kvalifikovaným jménem se to volat má.
 *
 * Filip to našel 8. 9. 2026 na dev při zkoušení přepínače přístupu ke Studiu:
 * stránka Supabase volala `supabase.publish` napřímo, takže u projektové
 * instance nešlo publikovat vůbec. `PublishPanel` to celou dobu dělal správně,
 * jen stránka Supabase má vlastní přepínač a pravidlo v něm chybělo — proto je
 * ta úvaha teď tady a ne dvakrát v komponentách.
 *
 * Projektová instance se pozná podle vazby z `findProjectBinding`, kterou modul
 * vrací ve výpisu: kanonická identita projektu (UUID) a `service_key` (klíč
 * služby ve stavu prostředí). Jméno instance se pak neposílá — cíl si ji najde
 * podle `service`. Historický tvar identity se do UI nedostane, server ho
 * převádí (`visibleProjectId`).
 */

export interface ProjectBinding {
  /** Kanonická identita projektu. `null` = projekt ji nemá čitelnou. */
  project_id: string | null;
  project_name?: string;
  service_key: string;
}

export interface ToolCall {
  tool: string;
  args: Record<string, unknown>;
}

/**
 * Postaví jméno nástroje a základní argumenty pro akci nad instancí.
 *
 * `binding.project_id === null` znamená „projekt nemá čitelnou identitu" —
 * jde se modulovou cestou, protože kvalifikace nečitelným jménem by skončila
 * horší chybou než původní. Identita, která odsud vyjde, je vždy kanonická:
 * historický tvar se do UI nedostane (řeší `visibleProjectId` na serveru).
 */
export function instanceToolCall(
  moduleName: string,
  action: string,
  instance: string,
  binding?: ProjectBinding | null,
): ToolCall {
  if (binding?.project_id && binding.service_key) {
    return {
      tool: `${binding.project_id}.${moduleName}.${action}`,
      args: { service: binding.service_key },
    };
  }
  // acm-self pojmenovává cíl jinak: bez modulového prefixu v hodnotě.
  if (moduleName === 'acm-self') {
    return { tool: `acm-self.${action}`, args: { target: instance.replace(/^acm-/, '') } };
  }
  return { tool: `${moduleName}.${action}`, args: { name: instance } };
}
